
import React, { useState, useEffect } from 'react';
import { Project, LogEntry, AppTheme } from '../types';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getForensicBrief } from '../services/geminiService';
import { PersistenceManager } from '../services/db';

interface ProjectOverviewProps {
  project: Project;
  onNavigateToLogs: () => void;
  theme: AppTheme;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({ project, onNavigateToLogs, theme }) => {
  const [brief, setBrief] = useState<string | null>(null);
  const [loadingBrief, setLoadingBrief] = useState(true);

  useEffect(() => {
    const fetchBrief = async () => {
      setLoadingBrief(true);
      const logs = await PersistenceManager.getProjectLogs(project.id);
      const forensicBrief = await getForensicBrief(project, logs);
      setBrief(forensicBrief);
      setLoadingBrief(false);
    };
    fetchBrief();
  }, [project.id]);

  const currentMarginLoss = project.expectedProfit - (project.marginTrajectory[project.marginTrajectory.length - 1]?.forecastedProfit || 0);

  const getPanelStyle = () => {
    switch(theme) {
      case AppTheme.BAMBOO: return "bg-[#faedcd] border-[#ccd5ae]";
      case AppTheme.OCEAN: return "bg-white border-[#d1e9ff]";
      default: return "bg-white border-black/5";
    }
  };

  const getAccentBtnStyle = () => {
    switch(theme) {
      case AppTheme.BAMBOO: return "bg-[#283618] text-[#fefae0] hover:bg-[#3a5a40]";
      case AppTheme.OCEAN: return "bg-[#003049] text-white hover:bg-[#004b73]";
      default: return "bg-black text-white hover:bg-slate-800";
    }
  };

  return (
    <div className="p-6 md:p-10 h-full overflow-y-auto max-w-6xl mx-auto space-y-10 animate-in fade-in duration-700">
      {/* Zen Header */}
      <div className="flex justify-between items-end border-b border-black/5 pb-6">
        <div className="min-w-0 flex-1 pr-6">
          <h1 className="text-3xl md:text-4xl font-extralight tracking-tight mb-2 truncate">{project.name}</h1>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className={`w-1 h-1 rounded-full ${theme === AppTheme.BAMBOO ? 'bg-[#283618]' : theme === AppTheme.OCEAN ? 'bg-[#003049]' : 'bg-black'}`}></span>
              <span className="font-black text-[8px] uppercase tracking-[0.2em] opacity-40">Profit Radar Active</span>
            </div>
          </div>
        </div>
        <button 
          onClick={onNavigateToLogs}
          className={`${getAccentBtnStyle()} px-6 py-3 rounded-2xl font-black text-[9px] uppercase tracking-[0.1em] transition-all duration-500 shadow-md active:scale-95 shrink-0`}
        >
          Audit Field Friction
        </button>
      </div>

      {/* Radar Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`${getPanelStyle()} p-6 rounded-[2rem] shadow-sm flex flex-col justify-between h-44 border group transition-all`}>
          <p className="text-[8px] font-black opacity-30 uppercase tracking-widest">Fee Retention</p>
          <div className="space-y-0.5">
            <span className="text-3xl font-extralight tracking-tighter">{project.marginRetentionScore}%</span>
            <div className="h-1 w-full bg-black/5 rounded-full overflow-hidden mt-2">
                <div className={`h-full transition-all duration-1000 ${project.marginStatus === 'Green' ? 'bg-emerald-400' : project.marginStatus === 'Yellow' ? 'bg-amber-400' : 'bg-rose-500'}`} style={{ width: `${project.marginRetentionScore}%` }}></div>
            </div>
          </div>
          <p className="text-[8px] opacity-40 font-bold uppercase tracking-tighter">Original Margin</p>
        </div>

        <div className={`${getPanelStyle()} p-6 rounded-[2rem] shadow-sm flex flex-col justify-between h-44 border-l-rose-500 border-l-4 border`}>
          <p className="text-[8px] font-black opacity-30 uppercase tracking-widest">Exposure</p>
          <span className="text-2xl font-bold tracking-tighter text-rose-600 truncate">${project.unbilledExposure.toLocaleString()}</span>
          <p className="text-[8px] opacity-40 font-bold uppercase tracking-tighter">Unbilled COs</p>
        </div>

        <div className={`${getPanelStyle()} p-6 rounded-[2rem] shadow-sm flex flex-col justify-between h-44 border`}>
          <p className="text-[8px] font-black opacity-30 uppercase tracking-widest">Baseline Fee</p>
          <span className="text-2xl font-extralight tracking-tighter truncate">${(project.expectedProfit / 1000000).toFixed(2)}M</span>
          <p className="text-[8px] opacity-40 font-bold uppercase tracking-tighter">Contract Target</p>
        </div>

        <div className={`${theme === AppTheme.ZEN ? 'bg-black text-white shadow-xl' : getPanelStyle() + ' border border-rose-500/10'} p-6 rounded-[2rem] flex flex-col justify-between h-44`}>
          <p className={`text-[8px] font-black uppercase tracking-widest ${theme === AppTheme.ZEN ? 'text-slate-500' : 'opacity-30'}`}>Forecast Risk</p>
          <span className={`text-2xl font-bold tracking-tighter truncate ${theme === AppTheme.ZEN ? 'text-white' : 'text-rose-600'}`}>-${(currentMarginLoss / 1000).toFixed(0)}K</span>
          <p className={`text-[8px] font-bold uppercase tracking-tighter ${theme === AppTheme.ZEN ? 'text-slate-500' : 'opacity-40'}`}>Erosion Delta</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-10">
        {/* Retention Chart */}
        <div className={`${getPanelStyle()} lg:col-span-2 p-8 rounded-[2.5rem] shadow-sm border`}>
          <div className="mb-6">
            <h3 className="text-lg font-light tracking-tight">Erosion Horizon</h3>
            <p className="text-[9px] opacity-40 font-medium">Automated Profit vs. Forecast Tracking</p>
          </div>
          <div className="h-[280px] -mx-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={project.marginTrajectory}>
                <defs>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="currentColor" stopOpacity={0.05}/>
                    <stop offset="95%" stopColor="currentColor" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="1 1" vertical={false} stroke="currentColor" opacity={0.03} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: 'currentColor', opacity: 0.2, fontWeight: 900, textTransform: 'uppercase', tracking: '0.1em' }} />
                <YAxis axisLine={false} tickLine={false} hide />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', fontSize: '10px', fontWeight: 600 }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '8px', paddingTop: '16px', fontWeight: 900, textTransform: 'uppercase', tracking: '0.1em', opacity: 0.2 }} />
                <Area type="monotone" dataKey="expectedProfit" stroke="currentColor" opacity={0.08} fill="transparent" strokeWidth={1} name="Target" />
                <Area type="monotone" dataKey="actualProfit" stroke="currentColor" fillOpacity={1} fill="url(#colorProfit)" strokeWidth={1.5} name="Actual" />
                <Area type="monotone" dataKey="forecastedProfit" stroke="#f43f5e" fill="transparent" strokeDasharray="4 4" strokeWidth={1.5} name="Warning" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Forensic Brief & Impacts */}
        <div className="space-y-6">
          <div className={`${getAccentBtnStyle()} p-8 rounded-[2.5rem] shadow-lg relative overflow-hidden group border border-white/5`}>
            <div className="relative z-10 space-y-4">
              <h3 className="text-[8px] font-black uppercase tracking-[0.2em] opacity-40 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${theme === AppTheme.BAMBOO ? 'bg-[#ccd5ae]' : 'bg-emerald-400'} shadow-sm`}></div>
                Forensic Brief
              </h3>
              {loadingBrief ? (
                <div className="space-y-2 py-2">
                  <div className="h-2 w-3/4 bg-white/10 rounded animate-pulse"></div>
                  <div className="h-2 w-1/2 bg-white/10 rounded animate-pulse"></div>
                </div>
              ) : (
                <p className="text-xs font-light opacity-90 leading-relaxed italic">
                  "{brief}"
                </p>
              )}
              <div className="pt-2 border-t border-white/5">
                <p className="text-[7px] font-black uppercase opacity-40 tracking-[0.3em]">Recommendation Status: Critical</p>
              </div>
            </div>
          </div>

          <div className={`${theme === AppTheme.ZEN ? 'bg-[#f2f2f2]' : 'bg-black/5'} p-8 rounded-[2.5rem] border border-white/60`}>
            <h3 className="text-[8px] font-black uppercase tracking-[0.2em] mb-6 opacity-60">Sub Profit impact</h3>
            <div className="space-y-6">
              {project.subcontractorImpacts.slice(0, 3).map((sub, i) => (
                <div key={i} className="flex justify-between items-center group">
                  <div className="min-w-0 pr-2">
                    <p className="text-[11px] font-bold truncate">{sub.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className={`w-1 h-1 rounded-full ${
                        sub.status === 'OnTrack' ? 'bg-emerald-400' : 
                        sub.status === 'AtRisk' ? 'bg-amber-400' : 'bg-rose-400'
                      }`}></div>
                      <p className="text-[7px] font-black opacity-30 uppercase tracking-widest">{sub.status}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xs font-bold tracking-tight ${sub.variance > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {sub.variance > 0 ? '+' : ''}${(Math.abs(sub.variance) / 1000).toFixed(0)}K
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOverview;
